import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import {
  CheckCircle2,
  ClipboardList,
  Copy,
  FileText,
  Loader2,
  PieChart,
  ShieldCheck,
  X,
  XCircle,
} from 'lucide-react'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import {
  primaryEntityActionButtonClass,
  secondaryEntityActionButtonClass,
} from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import { getParamInputType, getParamPlaceholder } from '../utils/modelCalculationParams'
import {
  getMissingParams,
  getRequiredParams,
  getValidationDetailMessage,
  useModelTestPkl,
  type ModelTestPklDialogProps,
} from '../hooks/useModelTestPkl'
import styles from '../styles/modelTestPkl.module.css'

export type { ModelTestPklDialogProps } from '../hooks/useModelTestPkl'

export function ModelTestPklDialog({
  isOpen,
  onClose,
  modelId,
  modelName,
  modelProcessorName,
  pklName,
  paramSelection,
  paramDefinitions,
}: ModelTestPklDialogProps & { isOpen: boolean; onClose: () => void }) {
  useBodyScrollLock(isOpen)

  const test = useModelTestPkl({
    isOpen,
    modelId,
    paramSelection,
    paramDefinitions,
  })

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const content = (
    <div className={styles.overlay} role="presentation" onClick={onClose}>
      <div
        className={styles.modelTestPklDialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="model-test-pkl-title"
        onClick={(event) => event.stopPropagation()}
        data-testid="model-test-pkl-dialog"
      >
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerIcon}>
              <FileText size={20} aria-hidden />
            </div>
            <div>
              <h2 id="model-test-pkl-title" className={styles.headerTitle}>
                Test Model Files
              </h2>
              <p className={styles.headerSubtitle}>{test.headerSubtitle}</p>
            </div>
          </div>
          <button type="button" className={styles.headerClose} onClick={onClose} aria-label="Close dialog">
            <X size={16} />
          </button>
        </div>

        <div className={styles.body}>
          {test.processing ? (
            <div className={styles.loading} aria-live="polite">
              <Loader2 className="h-5 w-5 animate-spin text-[#1570ef]" aria-hidden />
              <span>Testing model files…</span>
            </div>
          ) : null}

          {test.showResults ? (
            <div data-testid="model-test-pkl-results">
              <div
                className={cn(
                  styles.statusBanner,
                  test.allPassed ? styles.statusBannerSuccess : styles.statusBannerFailure,
                )}
              >
                <div className={styles.statusBannerMain}>
                  <div
                    className={cn(
                      styles.statusIconWrap,
                      test.allPassed ? styles.statusIconWrapPassed : styles.statusIconWrapFailed,
                    )}
                  >
                    {test.allPassed ? (
                      <CheckCircle2 size={28} aria-hidden />
                    ) : (
                      <XCircle size={28} aria-hidden />
                    )}
                  </div>
                  <div className={styles.statusBannerText}>
                    <h3>{test.allPassed ? 'Model file test passed!' : 'Model file test failed'}</h3>
                    <p>{test.resultMessage}</p>
                  </div>
                </div>
                {test.allPassed ? (
                  <div className={styles.statusBannerArt} aria-hidden>
                    <span className={cn(styles.statusArtBadge, styles.statusArtBadgePrimary)}>
                      <ClipboardList size={28} />
                    </span>
                    <span className={cn(styles.statusArtBadge, styles.statusArtBadgeSecondary)}>
                      <ShieldCheck size={22} />
                    </span>
                  </div>
                ) : null}
              </div>

              {test.validationTests.length > 0 ? (
                <>
                  <div className={styles.summaryCards}>
                    <div className={styles.summaryCard}>
                      <div className={cn(styles.summaryCardIcon, styles.summaryCardIconTotal)}>
                        <ClipboardList size={18} aria-hidden />
                      </div>
                      <span className={styles.summaryCardLabel}>Total Checks</span>
                      <strong className={styles.summaryCardValue}>{test.totalChecks}</strong>
                    </div>
                    <div className={styles.summaryCard}>
                      <div className={cn(styles.summaryCardIcon, styles.summaryCardIconPassed)}>
                        <CheckCircle2 size={18} aria-hidden />
                      </div>
                      <span className={styles.summaryCardLabel}>Passed</span>
                      <strong className={styles.summaryCardValue}>{test.passedCount}</strong>
                    </div>
                    <div className={styles.summaryCard}>
                      <div className={cn(styles.summaryCardIcon, styles.summaryCardIconFailed)}>
                        <XCircle size={18} aria-hidden />
                      </div>
                      <span className={styles.summaryCardLabel}>Failed</span>
                      <strong className={styles.summaryCardValue}>{test.failedCount}</strong>
                    </div>
                    <div className={styles.summaryCard}>
                      <div className={cn(styles.summaryCardIcon, styles.summaryCardIconRate)}>
                        <PieChart size={18} aria-hidden />
                      </div>
                      <span className={styles.summaryCardLabel}>Success Rate</span>
                      <strong className={styles.summaryCardValue}>{test.successRate}%</strong>
                    </div>
                  </div>

                  <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Validation Checks</h3>
                    <div className={styles.checkList}>
                      {test.validationTests.map((item) => (
                        <div key={item.name} className={styles.checkItem}>
                          <div
                            className={cn(
                              styles.checkItemStatus,
                              item.success ? styles.checkItemStatusPassed : styles.checkItemStatusFailed,
                            )}
                          >
                            {item.success ? (
                              <CheckCircle2 size={18} aria-hidden />
                            ) : (
                              <XCircle size={18} aria-hidden />
                            )}
                          </div>
                          <div className={styles.checkItemContent}>
                            <strong className={styles.checkItemName}>{item.name}</strong>
                            <span className={styles.checkItemMessage}>{item.message}</span>
                            {!item.success &&
                            (getMissingParams(item).length ||
                              getRequiredParams(item).length ||
                              getValidationDetailMessage(item)) ? (
                              <div className={styles.checkItemDetails}>
                                {getValidationDetailMessage(item) ? (
                                  <span className={styles.checkItemDetailsMessage}>
                                    {getValidationDetailMessage(item)}
                                  </span>
                                ) : null}
                                {getMissingParams(item).length ? (
                                  <div className={styles.checkItemParamGroup}>
                                    <span className={styles.checkItemParamLabel}>Missing params</span>
                                    <div className={styles.checkItemParamList}>
                                      {getMissingParams(item).map((param) => (
                                        <span
                                          key={param}
                                          className={cn(styles.checkItemParamChip, styles.checkItemParamChipMissing)}
                                        >
                                          {param}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                ) : null}
                                {getRequiredParams(item).length ? (
                                  <div className={styles.checkItemParamGroup}>
                                    <span className={styles.checkItemParamLabel}>Required params</span>
                                    <div className={styles.checkItemParamList}>
                                      {getRequiredParams(item).map((param) => (
                                        <span key={param} className={styles.checkItemParamChip}>
                                          {param}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                          <span
                            className={cn(
                              styles.checkItemBadge,
                              item.success ? styles.checkItemBadgePassed : styles.checkItemBadgeFailed,
                            )}
                          >
                            {item.success ? 'Passed' : 'Failed'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : null}

              {test.executionResult ? (
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>Execution Diagnostics</h3>
                  <div className={styles.jsonBlock}>
                    <div className={styles.jsonToolbar}>
                      <button
                        type="button"
                        className={styles.jsonCopyBtn}
                        onClick={() => void test.copyExecutionResult(test.executionResultJson)}
                      >
                        <Copy size={14} aria-hidden />
                        {test.jsonCopied ? 'Copied!' : 'Copy JSON'}
                      </button>
                    </div>
                    <pre className={styles.jsonContent}>{test.executionResultJson}</pre>
                  </div>
                </div>
              ) : null}

              {!test.validationTests.length && test.testError ? (
                <div className={styles.inlineError}>
                  {typeof test.testError === 'string' ? test.testError : test.testError.message}
                </div>
              ) : null}
            </div>
          ) : null}

          {!test.showResults && !test.processing && test.hasSelection ? (
            <div data-testid="model-test-pkl-form">
              {(modelName || modelProcessorName || pklName) && (
                <p className={styles.formIntro}>
                  Testing <strong>{modelName || 'model'}</strong>
                  {(modelProcessorName || pklName) && (
                    <>
                      {' '}
                      (
                      {modelProcessorName ? `processor: ${modelProcessorName}.py` : null}
                      {modelProcessorName && pklName ? ', ' : null}
                      {pklName ? `model: ${pklName}.pkl` : null}
                      )
                    </>
                  )}
                </p>
              )}

              <div className={styles.bulkFill}>
                <label htmlFor="test_bulk_values" className="mb-1 block text-sm font-medium text-slate-700">
                  Bulk insert values
                </label>
                <textarea
                  id="test_bulk_values"
                  rows={3}
                  className={cn(
                    'w-full rounded-lg border border-slate-200 px-3 py-2 text-sm',
                    styles.bulkTextarea,
                  )}
                  value={test.bulkValues}
                  onChange={(event) => test.setBulkValues(event.target.value)}
                  placeholder="Enter comma-separated values in field order (e.g. value1, value2, value3)"
                />
                <div className={styles.bulkActions}>
                  <button type="button" className={primaryEntityActionButtonClass} onClick={test.insertBulkValues}>
                    Insert
                  </button>
                  <button type="button" className={secondaryEntityActionButtonClass} onClick={test.clearAllFieldValues}>
                    Clear
                  </button>
                </div>
                {test.bulkValuesError ? <div className={styles.bulkError}>{test.bulkValuesError}</div> : null}
              </div>

              <form
                onSubmit={(event) => {
                  event.preventDefault()
                  void test.submitForm()
                }}
              >
                <div className={styles.formGrid}>
                  {test.applicationParamKeys.map((field) =>
                    paramSelection[field] ? (
                      <div key={field}>
                        <label htmlFor={`test_${field}`} className="mb-1 block text-sm font-medium text-slate-700">
                          {field}
                        </label>
                        {field === 'applicant_count' ? (
                          <select
                            id={`test_${field}`}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                            value={Number(test.formValues[field] ?? 1)}
                            onChange={(event) =>
                              test.updateApplicationField(field, Number(event.target.value))
                            }
                          >
                            {[1, 2, 3, 4, 5].map((count) => (
                              <option key={count} value={count}>
                                {count}
                              </option>
                            ))}
                          </select>
                        ) : getParamInputType(field, paramDefinitions) === 'checkbox' ? (
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              id={`test_${field}`}
                              type="checkbox"
                              checked={!!test.formValues[field]}
                              onChange={(event) => test.updateApplicationField(field, event.target.checked)}
                            />
                            {field}
                          </label>
                        ) : (
                          <input
                            id={`test_${field}`}
                            type={getParamInputType(field, paramDefinitions)}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                            value={String(test.formValues[field] ?? '')}
                            placeholder={getParamPlaceholder(field, paramDefinitions)}
                            onChange={(event) => test.updateApplicationField(field, event.target.value)}
                          />
                        )}
                        {test.submitted &&
                        field !== 'past_customer' &&
                        (test.formValues[field] === '' || test.formValues[field] == null) ? (
                          <div className={styles.fieldError}>{field} is required</div>
                        ) : null}
                      </div>
                    ) : null,
                  )}
                </div>

                {test.hasApplicantFields && Array.isArray(test.formValues.applicants)
                  ? test.formValues.applicants.map((applicant, index) => (
                      <div key={index} className={styles.applicantCard}>
                        <div className={styles.applicantCardTitle}>Applicant {index + 1}</div>
                        <div className={styles.formGrid}>
                          {test.applicantParamKeys.map((field) =>
                            paramSelection[field] ? (
                              <div key={`${field}_${index}`}>
                                <label
                                  htmlFor={`test_${field}_${index}`}
                                  className="mb-1 block text-sm font-medium text-slate-700"
                                >
                                  {field}
                                </label>
                                {getParamInputType(field, paramDefinitions) === 'checkbox' ? (
                                  <label className="flex items-center gap-2 text-sm">
                                    <input
                                      id={`test_${field}_${index}`}
                                      type="checkbox"
                                      checked={!!applicant[field]}
                                      onChange={(event) =>
                                        test.updateApplicantField(index, field, event.target.checked)
                                      }
                                    />
                                    {field}
                                  </label>
                                ) : (
                                  <input
                                    id={`test_${field}_${index}`}
                                    type={getParamInputType(field, paramDefinitions)}
                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                                    value={String(applicant[field] ?? '')}
                                    placeholder={getParamPlaceholder(field, paramDefinitions)}
                                    onChange={(event) =>
                                      test.updateApplicantField(index, field, event.target.value)
                                    }
                                  />
                                )}
                                {test.submitted &&
                                field !== 'past_customer' &&
                                (applicant[field] === '' || applicant[field] == null) ? (
                                  <div className={styles.fieldError}>{field} is required</div>
                                ) : null}
                              </div>
                            ) : null,
                          )}
                        </div>
                      </div>
                    ))
                  : null}

                <div className={cn(styles.footer, styles.footerForm)}>
                  <button type="button" className={secondaryEntityActionButtonClass} onClick={onClose}>
                    Close
                  </button>
                  <button
                    type="submit"
                    className={primaryEntityActionButtonClass}
                    disabled={test.processing || !test.isFormValid}
                  >
                    Run Test
                  </button>
                </div>
              </form>
            </div>
          ) : null}

          {!test.hasSelection && !test.processing ? (
            <div className={styles.inlineError}>
              Select at least one calculation variable before testing the model files.
            </div>
          ) : null}
        </div>

        {test.showResults ? (
          <div className={styles.footer}>
            <button type="button" className={secondaryEntityActionButtonClass} onClick={onClose}>
              Close
            </button>
            <button type="button" className={primaryEntityActionButtonClass} onClick={test.continueToForm}>
              Continue
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )

  return createPortal(content, document.body)
}
